import PropTypes from "prop-types"
import { connect } from "react-redux"
import { Link } from "react-router-dom"

import {
  displaySavedUserSearchHistory,
  saveUserSearchHistory,
  mayUpdateSearchSuggestions,
} from "app-store-v2/app/actions"
import {
  clearSearchUserInput,
  clearSearchSuggestions,
  fetchSearchSuggestions,
  toggleSearching,
  updateSearchValue,
} from "app-store-v2/search/actions"
import MatchQuery from "areas/header/jewlr/search/match-query"
import Image from "components/ui/image"
import { isTestMode } from "helpers/application"
import useWindowDimensions from "helpers/window-dimensions"

export default function withSearch(WrappedComponent) {
  const InternalWithSearch = (props) => {
    const { setShowSearchBar } = props
    const { isTablet } = useWindowDimensions()

    const handleOnClick = () => {
      if (setShowSearchBar) {
        setShowSearchBar(false)
      }
    }

    const getSuggestionValue = () => {
      return ""
    }

    const renderSuggestion = (suggestion, { query }) => {
      const name = (
        <MatchQuery
          keyPrefix={`search-suggestion-${suggestion.keyword}-`}
          name={suggestion.name}
          query={query}
        />
      )

      if (!isTablet) {
        return (
          <Link
            aria-label={suggestion.name}
            className="block"
            data-lc="with-search"
            to={suggestion.url}
          >
            <div>
              <div className="border-grey-10 border">
                <Image
                  alt={suggestion.name}
                  height="98"
                  srcSet={suggestion.image_url}
                  width="98"
                />
              </div>
              <h3 className="text-10 mt-6 text-center font-medium">{name}</h3>
            </div>
          </Link>
        )
      }

      return (
        <Link
          className="flex items-center py-8 focus:-outline-offset-2"
          data-lc="with-search"
          onClick={handleOnClick}
          to={suggestion.url}
        >
          {suggestion.image_url && (
            <Image
              className="mr-12"
              height="64"
              src={suggestion.image_url}
              width="64"
            />
          )}

          {suggestion.subtitle ? (
            <div className="w-3/4">
              <p className="text-12 italic">{name}</p>
              <p className="text-12 italic">{suggestion.subtitle}</p>
            </div>
          ) : (
            <p className="text-12 ml-12 italic">{name}</p>
          )}
        </Link>
      )
    }

    return (
      <WrappedComponent
        {...props}
        getSuggestionValue={getSuggestionValue}
        renderSuggestion={renderSuggestion}
      />
    )
  }

  const mapStateToProps = (state) => {
    const { searching, suggestions, value } = state.search

    return {
      searching,
      suggestions,
      value,
    }
  }

  const mapDispatchToProps = (dispatch) => {
    return {
      clearSearch: () => {
        dispatch(toggleSearching(false))
        dispatch(clearSearchUserInput())
      },
      clearSearchSuggestions: () => {
        dispatch(clearSearchSuggestions())
      },
      displaySavedSearchHistory: () => {
        dispatch(displaySavedUserSearchHistory())
      },
      onChange: (e) => {
        dispatch(updateSearchValue(e.target.value || ""))
      },
      onSuggestionsClearRequested: () => {
        dispatch(toggleSearching(false))
        dispatch(clearSearchSuggestions())
      },
      onSuggestionsFetchRequested: ({ value }) => {
        const query = (value || "").trim()
        if (query.length > 2) {
          dispatch(toggleSearching(true))
          dispatch(fetchSearchSuggestions(query))
        } else {
          dispatch(toggleSearching(false))
          dispatch(mayUpdateSearchSuggestions([], query))
        }
      },
      saveSearchHistory: (query) => {
        dispatch(saveUserSearchHistory(query))
        if (!isTestMode) {
          dataLayer.push({
            event: "search",
            searchString: query,
          })
        }
      },
      toggleSearching: (searching) => {
        dispatch(toggleSearching(searching))
      },
    }
  }

  InternalWithSearch.propTypes = {
    setShowSearchBar: PropTypes.func,
  }

  const WithSearch = connect(
    mapStateToProps,
    mapDispatchToProps
  )(InternalWithSearch)

  return WithSearch
}
